'use client'
// AgentMeshPanel — Command Center : état du réseau de nœuds GPU (GPU Scheduler).
// Consomme GET /api/gpu-nodes (VRAM, utilisation, tâches, tokens cumulés, live).
// Auto-refresh 5 s. Brique n°1 du Mission Control.
import { useEffect, useState } from 'react'
import { SectionLabel } from '@/components/landing/SectionLabel'

interface GpuNode {
  id: string
  hostname: string
  gpuName?: string | null
  gpuVendor?: string | null
  gpuVramTotal?: number | null   // Go
  gpuVramFree?: number | null    // Go
  gpuUtil?: number | null        // %
  tier?: string | null
  status: string                 // online | offline
  pulledModels: string[]
  platform?: string | null
  tasksDone?: number | null
  tasksFailed?: number | null
  totalTokens?: number | null
  live: boolean
  liveStats?: { tokensPerSec?: number; gpuTempC?: number } | null
}

const fmt = (n?: number | null, d = 1) => (n == null ? '—' : n.toFixed(d))
const fmtInt = (n?: number | null) => (n == null ? '0' : n.toLocaleString('fr-CA'))

function statusColor(n: GpuNode) {
  if (n.live) return 'var(--teal)'
  if (n.status === 'online') return 'var(--accent)'
  return 'var(--text-muted)'
}

function NodeCard({ n }: { n: GpuNode }) {
  const total = n.gpuVramTotal ?? 0
  const free  = n.gpuVramFree ?? 0
  const used  = Math.max(0, total - free)
  const usedPct = total > 0 ? (used / total) * 100 : 0
  const util = n.gpuUtil ?? null
  const color = statusColor(n)
  const tokPerSec = n.liveStats?.tokensPerSec
  const tempC = n.liveStats?.gpuTempC

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: 'var(--bg-card)', border: `1px solid ${n.live ? 'rgba(45,212,191,0.28)' : 'var(--border-dim)'}`,
      borderRadius: 12, padding: '14px 16px',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}`, flexShrink: 0 }} />
        <span className="font-display" style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {n.hostname}
        </span>
        <span style={{
          marginLeft: 'auto', fontSize: 8, fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '0.08em',
          color, background: `${color}14`, border: `1px solid ${color}38`, borderRadius: 8, padding: '2px 7px',
        }}>
          {n.live ? 'LIVE' : n.status.toUpperCase()}
        </span>
      </div>

      <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 10 }}>
        {n.gpuName ?? 'GPU inconnu'}{n.tier ? ` · ${n.tier}` : ''}{n.platform ? ` · ${n.platform}` : ''}
      </div>

      {/* VRAM */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 8, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>VRAM</span>
          <span style={{ fontSize: 8, color: 'var(--white)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
            {fmt(used)} / {fmt(total)} Go
          </span>
        </div>
        <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${usedPct}%`, background: usedPct > 85 ? 'var(--coral)' : color, borderRadius: 3, transition: 'width 0.6s ease' }} />
        </div>
      </div>

      {/* Métriques */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 10 }}>
        <Metric label="GPU" value={util != null ? `${fmt(util, 0)}%` : '—'} />
        <Metric label="tok/s" value={tokPerSec != null ? fmt(tokPerSec, 0) : '—'} dim={tokPerSec == null} />
        <Metric label="temp" value={tempC != null ? `${fmt(tempC, 0)}°` : '—'} dim={tempC == null} />
        <Metric label="tâches" value={fmtInt(n.tasksDone)} />
        <Metric label="échecs" value={fmtInt(n.tasksFailed)} color={(n.tasksFailed ?? 0) > 0 ? 'var(--coral)' : undefined} />
        <Metric label="tokens" value={fmtInt(n.totalTokens)} />
      </div>

      {n.pulledModels?.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {n.pulledModels.slice(0, 4).map(m => (
            <span key={m} style={{ fontSize: 8, padding: '1px 6px', borderRadius: 4, background: 'rgba(79,124,255,0.1)', color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
              {m}
            </span>
          ))}
          {n.pulledModels.length > 4 && (
            <span style={{ fontSize: 8, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>+{n.pulledModels.length - 4}</span>
          )}
        </div>
      )}
    </div>
  )
}

function Metric({ label, value, color, dim }: { label: string; value: string; color?: string; dim?: boolean }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-dim)', borderRadius: 8, padding: '6px 4px', textAlign: 'center' }}>
      <div style={{ fontSize: 12, fontWeight: 800, fontFamily: 'var(--font-mono)', color: dim ? 'var(--text-muted)' : (color ?? 'var(--white)') }}>{value}</div>
      <div style={{ fontSize: 7, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.06em' }}>{label}</div>
    </div>
  )
}

export function AgentMeshPanel() {
  const [nodes, setNodes] = useState<GpuNode[]>([])
  const [activeCount, setActiveCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const r = await fetch('/api/gpu-nodes')
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const d = await r.json()
        if (!alive) return
        setNodes(d.nodes ?? [])
        setActiveCount(d.activeCount ?? 0)
        setError(null)
      } catch (e: any) {
        if (alive) setError(e.message)
      } finally {
        if (alive) setLoading(false)
      }
    }
    load()
    const iv = setInterval(load, 5_000)
    return () => { alive = false; clearInterval(iv) }
  }, [])

  const totalVram = nodes.reduce((s, n) => s + (n.gpuVramTotal ?? 0), 0)
  const freeVram  = nodes.reduce((s, n) => s + (n.gpuVramFree ?? 0), 0)

  return (
    <section style={{ marginBottom: '48px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <SectionLabel>Agent Mesh — GPU</SectionLabel>
        <span style={{ marginLeft: 'auto', fontSize: 10, fontFamily: 'var(--font-mono)', color: activeCount > 0 ? 'var(--teal)' : 'var(--text-muted)' }}>
          {activeCount} live · {nodes.length} nœud{nodes.length > 1 ? 's' : ''} · {fmt(totalVram - freeVram)} / {fmt(totalVram)} Go VRAM
        </span>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>Interrogation du réseau GPU…</p>
      ) : error ? (
        <p style={{ color: 'var(--coral)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>GPU Scheduler indisponible : {error}</p>
      ) : nodes.length === 0 ? (
        <div style={{ padding: '18px', background: 'rgba(79,124,255,0.03)', border: '1px solid rgba(79,124,255,0.12)', borderRadius: 10, fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          Aucun nœud GPU enregistré. Lancez un worker desktop (installateur) pour rejoindre le réseau.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {nodes.map(n => <NodeCard key={n.id} n={n} />)}
        </div>
      )}
    </section>
  )
}
